import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Box,
  Link
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { format } from 'date-fns';

interface CredentialAlert {
  counsellorId: string;
  name: string;
  expiringCredentials: Array<{
    type: string;
    expiryDate: string;
    daysUntilExpiry: number;
    reference?: string;
  }>;
}

const CredentialExpiryAlerts: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<CredentialAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/dashboard/credential-alerts');
      setAlerts(response.data);
    } catch (err: any) {
      console.error('Error loading credential alerts:', err);
      setError('Failed to load credential expiry alerts');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (daysUntilExpiry: number): 'error' | 'warning' | 'info' => {
    if (daysUntilExpiry <= 7) return 'error';
    if (daysUntilExpiry <= 14) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <WarningIcon sx={{ color: '#FFB74D' }} />
          <Typography variant="h6" className="theme-text-primary">
            Credentials Expiring in Next 30 Days
          </Typography>
        </Box>

        {alerts.length === 0 ? (
          <Alert severity="success" sx={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}>
            No credentials expiring in the next 30 days
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                <TableRow>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Counsellor
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Credential Type
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Reference
                  </TableCell>
                  <TableCell sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Expiry Date
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Days Remaining
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.9)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    Urgency
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => (
                  alert.expiringCredentials.map((credential, index) => (
                    <TableRow key={`${alert.counsellorId}-${credential.type}-${index}`}>
                      {index === 0 && (
                        <TableCell 
                          rowSpan={alert.expiringCredentials.length}
                          sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
                        >
                          <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate(`/counsellor-details/${alert.counsellorId}`)}
                            sx={{
                              color: '#fafafa',
                              textDecoration: 'none',
                              fontWeight: 500,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: '#5568d3'
                              }
                            }}
                          >
                            {alert.name}
                          </Link>
                        </TableCell>
                      )}
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Chip 
                          label={credential.type} 
                          size="small"
                          color={credential.type === 'DBS' ? 'primary' : 'secondary'}
                        />
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        {credential.reference || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        {credential.expiryDate ? format(new Date(credential.expiryDate), 'dd MMM yyyy') : 'N/A'}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Typography 
                          variant="body2" 
                          fontWeight="bold"
                          sx={{ 
                            color: credential.daysUntilExpiry <= 7 ? '#EF5350' : 
                                   credential.daysUntilExpiry <= 14 ? '#FFB74D' : 
                                   'rgba(255, 255, 255, 0.9)'
                          }}
                        >
                          {credential.daysUntilExpiry} days
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <Chip
                          label={
                            credential.daysUntilExpiry <= 7 ? 'URGENT' :
                            credential.daysUntilExpiry <= 14 ? 'High' :
                            'Medium'
                          }
                          color={getUrgencyColor(credential.daysUntilExpiry)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default CredentialExpiryAlerts;
